import React, { useRef, useEffect, useState, useCallback } from 'react'

// Plays the remote audio stream for voice calls.
// Must be a separate component so the ref and srcObject sync
// work correctly even when the stream arrives after first render.
function RemoteAudio({ stream }) {
  const ref = useRef(null)
  useEffect(() => {
    if (ref.current) ref.current.srcObject = stream || null
  }, [stream])
  if (!stream) return null
  return <audio ref={ref} autoPlay playsInline />
}

function VideoTile({ stream, mirrored, className }) {
  const ref = useRef(null)
  useEffect(() => {
    const el = ref.current
    if (el) el.srcObject = stream || null
  }, [stream])
  if (!stream) return null
  return (
    <video
      ref={ref}
      autoPlay
      playsInline
      muted={mirrored}
      className={className}
      style={mirrored ? { transform: 'scaleX(-1)' } : undefined}
    />
  )
}

function SessionTimer({ timeLimitSeconds, callStartedAtMs }) {
  const [remaining, setRemaining] = useState(null)
  useEffect(() => {
    if (!timeLimitSeconds || !callStartedAtMs) return
    const tick = () => {
      const elapsed = Math.floor((Date.now() - callStartedAtMs) / 1000)
      setRemaining(Math.max(0, timeLimitSeconds - elapsed))
    }
    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [timeLimitSeconds, callStartedAtMs])
  if (remaining === null) return null
  const mins = Math.floor(remaining / 60)
  const secs = remaining % 60
  const low  = remaining <= 5 * 60
  return (
    <div className={`session-timer ${low ? 'low' : ''}`}>
      {mins}:{String(secs).padStart(2, '0')}
    </div>
  )
}

// ── Speaker toggle ────────────────────────────────────────────
// Works for both voice calls (audio element) and video calls
// (video element). Switches output between speaker and earpiece
// via setSinkId() — supported in Chrome/WebView on Android and
// iOS WKWebView (iOS 17+). Falls back silently if unsupported.
// Android-only speaker toggle — iOS uses the system audio route
// button in Control Center (no custom button needed there).
function useAndroidSpeaker(voiceOnly) {
  const isAndroid = typeof navigator !== 'undefined' &&
    /android/i.test(navigator.userAgent || '')
  const [speakerOn, setSpeakerOn] = useState(!voiceOnly)

  const toggle = useCallback(() => {
    const next = !speakerOn
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ type: 'set-speaker', speakerOn: next })
      )
    }
    setSpeakerOn(next)
  }, [speakerOn])

  return { isAndroid, speakerOn, toggle }
}

export default function CallView({
  call, voiceOnly, taskTitle,
  whiteboardOpen, onToggleWhiteboard,
  onLeave,
  confirmingLeave, onRequestLeaveConfirm, onCancelLeaveConfirm,
  timeLimitSeconds, callStartedAtMs,
}) {
  const connected = call.connectionState === 'connected'
  const { isAndroid, speakerOn, toggle: toggleSpeaker } = useAndroidSpeaker(voiceOnly)

  return (
    <div className="call-container">
      <div className="call-header">
        <span>{taskTitle || (voiceOnly ? 'Voice Call' : 'Video Call')}</span>
        <SessionTimer timeLimitSeconds={timeLimitSeconds} callStartedAtMs={callStartedAtMs} />
      </div>

      {voiceOnly ? (
        <div className="voice-center">
          {/* Audio element plays the remote audio track.
              autoPlay is required — without it the audio track
              is received but never played. muted=false so the
              remote voice is audible. */}
          <RemoteAudio stream={call.remoteStream} />
          <div className="voice-avatar">🎙️</div>
          <div className="voice-status">
            {call.remoteStream
              ? 'Connected'
              : call.connectionState === 'failed'
                ? 'Connection failed'
                : 'Waiting for the other person to join...'}
          </div>
        </div>
      ) : (
        <>
          <div className="remote-container">
            {call.remoteStream ? (
              <VideoTile stream={call.remoteStream} className="remote-video cover" />
            ) : (
              <div className="waiting-box">
                <div className="spinner" />
                <span>
                  {call.connectionState === 'failed'
                    ? 'Connection failed — please try again.'
                    : 'Waiting for the other person to join...'}
                </span>
              </div>
            )}
          </div>

          {call.cameraEnabled && call.localStream && (
            <div className="self-view">
              <VideoTile stream={call.localStream} mirrored className="self-video" />
            </div>
          )}
        </>
      )}

      {/* Controls — chat and screen share removed */}
      <div className="controls">

        {/* Microphone */}
        <button
          className={`ctrl-btn ${!call.micEnabled ? 'off' : ''}`}
          onClick={call.toggleMic}
          title={call.micEnabled ? 'Mute microphone' : 'Unmute microphone'}
        >
          {call.micEnabled ? '🎤' : '🔇'}
        </button>

        {/* Speaker toggle — Android only (iOS uses system Control Center) */}
        {isAndroid && (
          <button
            className={`ctrl-btn ${speakerOn ? 'active' : ''}`}
            onClick={toggleSpeaker}
            title={speakerOn ? 'Switch to earpiece' : 'Switch to speaker'}
          >
            {speakerOn ? '🔊' : '🔈'}
          </button>
        )}

        {/* Hang up */}
        <button className="leave-btn" onClick={onRequestLeaveConfirm}>📞</button>

        {/* Camera — video calls only */}
        {!voiceOnly && (
          <button
            className={`ctrl-btn ${!call.cameraEnabled ? 'off' : ''}`}
            onClick={call.toggleCamera}
            title={call.cameraEnabled ? 'Turn off camera' : 'Turn on camera'}
          >
            {call.cameraEnabled ? '📹' : '🚫'}
          </button>
        )}

        {/* Whiteboard */}
        <button
          className="ctrl-btn"
          onClick={onToggleWhiteboard}
          title="Whiteboard"
        >
          🖊️
        </button>

      </div>

      {!connected && call.connectionState !== 'failed' && (
        <div className="connecting-banner">
          <div className="spinner small" /> Connecting...
        </div>
      )}

      {confirmingLeave && (
        <div className="leave-modal-overlay" onClick={onCancelLeaveConfirm}>
          <div className="leave-modal" onClick={(e) => e.stopPropagation()}>
            <h4>Leave the call?</h4>
            <p>The other person will be disconnected too.</p>
            <div className="leave-modal-row">
              <button className="leave-modal-cancel" onClick={onCancelLeaveConfirm}>Cancel</button>
              <button className="leave-modal-confirm" onClick={onLeave}>Leave</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}